import React from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { AuxNotificationGroup } from "@blk/aladdin-react-components-es";
import { AuxButtonTypeEnum, AuxNotificationFooterTypeEnum, AuxNotificationStyleEnum } from "@blk/aladdin-web-components";
import { language } from "language/default";
import "./insErrorBoundary.scss";
const ERROR_SEVERITY_LEVEL = 3;
/**
 * Default fallback component shown when an error occurs
 * @param root0 - The component props
 * @param root0.error - The error that was caught
 * @param root0.resetErrorBoundary - Function to reset the error boundary
 */
const DefaultErrorFallback = ({ error, resetErrorBoundary }) => {
    const errorMessage = process.env.NODE_ENV === "development"
        ? language.en.errorBoundary.developmentMessage(error.message)
        : language.en.errorBoundary.message;
    return (React.createElement("div", { className: "ins-error-boundary", role: "alert" },
        React.createElement(AuxNotificationGroup, { config: [
                {
                    buttons: [
                        {
                            buttonType: AuxButtonTypeEnum.PRIMARY,
                            clickCallback: resetErrorBoundary,
                            label: language.en.errorBoundary.tryAgain,
                            type: AuxNotificationFooterTypeEnum.BUTTON
                        },
                        {
                            buttonType: AuxButtonTypeEnum.SECONDARY,
                            clickCallback: () => {
                                window.location.href = "/";
                            },
                            label: language.en.errorBoundary.goToHome,
                            type: AuxNotificationFooterTypeEnum.BUTTON
                        }
                    ],
                    id: "error-boundary",
                    message: errorMessage,
                    notificationStyle: AuxNotificationStyleEnum.ERROR,
                    showCloseIcon: false
                }
            ], openOnLoad: true })));
};
/**
 * Global error boundary component that catches errors and displays fallback UI
 * Also logs errors to Application Insights
 * @param root0 - The component props
 * @param root0.children - Child components to wrap
 * @param root0.fallback - Custom fallback component to display on error
 * @param root0.onReset - Optional callback when error boundary is reset
 * @param root0.resetKeys - Keys that trigger a reset when changed
 */
export const InsErrorBoundary = ({ children, fallback: FallbackComponent = DefaultErrorFallback, onReset, resetKeys = [] }) => {
    const appInsights = useAppInsightsContext();
    const handleError = (error, info) => {
        // Log to Application Insights
        appInsights.trackException({
            error,
            properties: {
                componentStack: info.componentStack,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent
            },
            severityLevel: ERROR_SEVERITY_LEVEL
        });
        // Log to console in development
        if (process.env.NODE_ENV === "development") {
            // eslint-disable-next-line no-console
            console.error("Error caught by boundary:", error);
            // eslint-disable-next-line no-console
            console.error("Component stack:", info.componentStack);
        }
    };
    const handleReset = () => {
        // Call custom reset handler if provided
        if (onReset) {
            onReset();
        }
    };
    return (React.createElement(ReactErrorBoundary, { FallbackComponent: FallbackComponent, onError: handleError, onReset: handleReset, resetKeys: resetKeys }, children));
};
